import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import { Box } from 'grommet';
import { Seo } from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { CurrentDateContext, useAppTheme } from '@Components/Context';
import { AuthNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import {
  EventDetailsDemoContentPartial, EventDetailsPartial, PiracyStreamsPartial,
} from '@Components/Partial/Product/Cyclops';

import {
  initiateCyclopsLivestreamDetailsRequest,
  clearCyclopsLivestreamDetailsRequest,
  startOrStopCyclopsLivestreamRequest,
  bypassPipelineCyclopsLivestreamRequest,
  initiateCyclopsCompanyOrganizationsRequest,
  setCyclopsActiveOrganizationRequest,
  initiateCyclopsPiracyStreamsRequest,
  clearCyclopsPiracyStreamsRequest,
  killCyclopsPiracyStreamRequest,
  startStreamRippersCyclopsLivestreamRequest,
} from '@Actions';

const CyclopsEventDetailsPage = ({
  small,
  demoOnly,
  location,
  loading,
  submitLoading,
  fetchCompanyOrganizations,
  setActiveOrganization,
  fetchLivestreamDetails,
  clearLivestreamDetails,
  startOrStopLivestream,
  bypassPipelineLivestream,
  fetchPiracyStreams,
  clearPiracyStreams,
  killPiracyStream,
  piracyStreamsLoading,
  startStreamRippers,
  livestreamDetails = null,
  piracyStreamData = null,
  companyOrganizations = null,
  activeOrganization = null,
  companyDemoData = null,
}) => {
  const { id: eventUid } = useParams();
  const { today } = React.useContext(CurrentDateContext);
  const formattedDate = today.toLocaleDateString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric',
  });
  const { theme: { page } } = useAppTheme();

  React.useEffect(() => {
    if (!demoOnly) {
      fetchLivestreamDetails(eventUid);
      /* eslint-disable camelcase */
      fetchPiracyStreams({ live_stream_id: eventUid });
      /* eslint-enable camelcase */
    }

    if (!demoOnly && (!companyOrganizations || companyOrganizations?.length === 0)) {
      fetchCompanyOrganizations();
    }

    return () => {
      if (!demoOnly) {
        clearLivestreamDetails();
        clearPiracyStreams();
      }
    };
  }, []);

  React.useEffect(() => {
    if (companyOrganizations?.length > 0 && !activeOrganization && livestreamDetails) {
      const currentOrg = _.find(companyOrganizations, { Id: livestreamDetails?.contentOwnerId });
      setActiveOrganization({ uid: currentOrg.Id, name: currentOrg.name });
    }
  }, [companyOrganizations, livestreamDetails]);

  const handleStartOrStopLivestream = (streamId, val) => {
    startOrStopLivestream({ Id: streamId, start: val }, paths.cyclopsEventDetails.replace(':id', eventUid));
  };

  const handleBypassPipelineLivestream = (streamId) => {
    bypassPipelineLivestream({ Id: streamId }, paths.cyclopsEventDetails.replace(':id', eventUid));
  };

  const handleStartStreamRippers = (streamId) => {
    startStreamRippers({ Id: streamId }, paths.cyclopsEventDetails.replace(':id', eventUid));
  };

  const renderPageContent = () => {
    if (demoOnly) {
      return (
        <EventDetailsDemoContentPartial
          small={small}
          formattedDate={formattedDate}
          eventUid={eventUid}
          companyDemoData={companyDemoData}
        />
      );
    }

    return (
      <Box flex background={page.background} pad={small ? '2rem' : '3rem'} direction="column" gap="1.5rem">
        <EventDetailsPartial
          small={small}
          loading={loading || submitLoading}
          formattedDate={formattedDate}
          eventUid={eventUid}
          eventData={livestreamDetails}
          activeOrganization={activeOrganization}
          startOrStopLivestream={handleStartOrStopLivestream}
          bypassPipelineLivestream={handleBypassPipelineLivestream}
          startStreamRippers={handleStartStreamRippers}
          companyOrganizations={companyOrganizations}
        />
        <PiracyStreamsPartial
          small={small}
          loading={piracyStreamsLoading || submitLoading}
          piracyStreamData={piracyStreamData}
          killPiracyStream={killPiracyStream}
        />
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      hideBanner
      small={small}
      location={location}
    >
      <Seo />
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    activeOrganization: state.organizations.activeCompanyOrganization,
    livestreamDetails: state.cyclops.livestreamDetails,
    companyOrganizations: state.cyclops.companyOrganizations,
    piracyStreamData: state.cyclops.piracyStreams,
    loading: state.fetchLoader.dataLoading,
    piracyStreamsLoading: state.fetchLoader.secondaryDataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchLivestreamDetails: initiateCyclopsLivestreamDetailsRequest,
    clearLivestreamDetails: clearCyclopsLivestreamDetailsRequest,
    startOrStopLivestream: startOrStopCyclopsLivestreamRequest,
    bypassPipelineLivestream: bypassPipelineCyclopsLivestreamRequest,
    fetchCompanyOrganizations: initiateCyclopsCompanyOrganizationsRequest,
    setActiveOrganization: setCyclopsActiveOrganizationRequest,
    fetchPiracyStreams: initiateCyclopsPiracyStreamsRequest,
    clearPiracyStreams: clearCyclopsPiracyStreamsRequest,
    killPiracyStream: killCyclopsPiracyStreamRequest,
    startStreamRippers: startStreamRippersCyclopsLivestreamRequest,
  }, dispatch);
}

CyclopsEventDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  demoOnly: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  companyDemoData: PropTypes.objectOf(PropTypes.any),
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  piracyStreamsLoading: PropTypes.bool.isRequired,
  livestreamDetails: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    maxVideoHeight: PropTypes.string,
    maxVideoWidth: PropTypes.string,
    contentOwnerId: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    encoderOutboundProtocol: PropTypes.string.isRequired,
    urlOrCidrs: PropTypes.string,
    currentOriginManifestPath: PropTypes.string.isRequired,
    currentHostVideoBaseFolder: PropTypes.string.isRequired,
    clientMediaId: PropTypes.string,
    protocolConfig: PropTypes.object,
    endDateTime: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    startDateTime: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    expectedViewership: PropTypes.number.isRequired,
    backupOriginUrl: PropTypes.string,
    piracySearchStatus: PropTypes.string,
  }),
  piracyStreamData: PropTypes.arrayOf(PropTypes.shape({
    piracyUrl: PropTypes.string.isRequired,
    liveStreamId: PropTypes.string.isRequired,
    pirateId: PropTypes.string.isRequired,
    linkToS3CapturedFootage: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    cdn: PropTypes.string,
    metrics: PropTypes.shape({
      viewers: PropTypes.number,
      timeToKill: PropTypes.number,
      foundAt: PropTypes.number,
      shutdownAt: PropTypes.number,
      decodedAt: PropTypes.number,
    }).isRequired,
    decodedWatermarkId: PropTypes.string,
  })),
  companyOrganizations: PropTypes.arrayOf(PropTypes.any),
  activeOrganization: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  fetchLivestreamDetails: PropTypes.func.isRequired,
  clearLivestreamDetails: PropTypes.func.isRequired,
  startOrStopLivestream: PropTypes.func.isRequired,
  bypassPipelineLivestream: PropTypes.func.isRequired,
  fetchPiracyStreams: PropTypes.func.isRequired,
  clearPiracyStreams: PropTypes.func.isRequired,
  killPiracyStream: PropTypes.func.isRequired,
  fetchCompanyOrganizations: PropTypes.func.isRequired,
  setActiveOrganization: PropTypes.func.isRequired,
  startStreamRippers: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withProductAuth(CyclopsEventDetailsPage),
);
